/**
 * Centralized Role Configuration
 *
 * Single source of truth for role definitions, ensuring consistency across:
 * - User Management dropdowns
 * - Permission Management displays
 * - API validation
 * - Role assignments
 */
import { RoleLevel, ROLE_LABELS } from '../types/rbacImproved';
/**
 * Valid roles that can be assigned by admins
 * Must match API validation in: api/admin-update-user-role/index.js line 60
 *
 * These are roles that admins can actively assign to users.
 */
export const ASSIGNABLE_ROLES = [
    RoleLevel.DEALER_APPLICANT,
    RoleLevel.BASIC_DEALER,
    RoleLevel.VERIFIED_DEALER,
    RoleLevel.SENIOR_DEALER,
    RoleLevel.SALES_MANAGER,
    RoleLevel.PLATFORM_ADMIN,
    RoleLevel.SUPER_ADMIN
];
/**
 * System state roles that users can have but admins cannot assign
 * These are managed by system logic, not manual assignment.
 */
export const SYSTEM_STATE_ROLES = [
    RoleLevel.SUSPENDED, // Managed by suspension actions
    RoleLevel.GUEST // Default state for unauthenticated/new users
];
/**
 * Deprecated or unused roles
 */
export const DEPRECATED_ROLES = [
    RoleLevel.REGION_ADMIN // Not implemented in current system
];
/**
 * All active roles (excludes deprecated)
 */
export const ALL_ACTIVE_ROLES = [
    ...SYSTEM_STATE_ROLES,
    ...ASSIGNABLE_ROLES
];
/**
 * Get role options for admin dropdowns
 * Optionally filtered by max assignable role level
 */
export const getAssignableRoleOptions = (maxLevel) => {
    const roles = maxLevel
        ? ASSIGNABLE_ROLES.filter(role => role <= maxLevel)
        : ASSIGNABLE_ROLES;
    return roles.map(role => ({
        value: getRoleEnumKey(role),
        label: ROLE_LABELS[role],
        level: role
    }));
};
/**
 * Get all role options for display (Permission Matrix, etc.)
 */
export const getAllRoleOptions = () => {
    return ALL_ACTIVE_ROLES.map(role => ({
        value: getRoleEnumKey(role),
        label: ROLE_LABELS[role],
        level: role,
        isAssignable: ASSIGNABLE_ROLES.includes(role),
        isSystemState: SYSTEM_STATE_ROLES.includes(role)
    }));
};
/**
 * Convert RoleLevel enum value to string key name
 * Example: RoleLevel.PLATFORM_ADMIN (9) → "PLATFORM_ADMIN"
 */
export const getRoleEnumKey = (roleLevel) => {
    const entry = Object.entries(RoleLevel)
        .find(([key, val]) => val === roleLevel && isNaN(Number(key)));
    return entry?.[0] || 'GUEST';
};
/**
 * Convert role string key to RoleLevel enum value
 * Example: "PLATFORM_ADMIN" → RoleLevel.PLATFORM_ADMIN (9)
 */
export const getRoleLevelFromKey = (roleKey) => {
    return RoleLevel[roleKey] || RoleLevel.GUEST;
};
/**
 * Validate if a role is assignable by admins
 */
export const isAssignableRole = (roleLevel) => {
    return ASSIGNABLE_ROLES.includes(roleLevel);
};
/**
 * Validate if a role is a system state
 */
export const isSystemStateRole = (roleLevel) => {
    return SYSTEM_STATE_ROLES.includes(roleLevel);
};
/**
 * Check if a role name is valid according to API
 */
export const isValidRoleName = (roleName) => {
    const roleLevel = getRoleLevelFromKey(roleName);
    return ASSIGNABLE_ROLES.includes(roleLevel);
};
/**
 * Get role color for badges (consistent across app)
 */
export const getRoleColor = (roleLevel) => {
    if (roleLevel === RoleLevel.SUSPENDED)
        return '#dc2626'; // Red
    if (roleLevel === RoleLevel.GUEST)
        return '#6b7280'; // Gray
    if (roleLevel >= RoleLevel.PLATFORM_ADMIN)
        return '#ef4444'; // Red for high admins
    if (roleLevel >= RoleLevel.SALES_MANAGER)
        return '#f59e0b'; // Orange for managers
    if (roleLevel >= RoleLevel.SENIOR_DEALER)
        return '#3b82f6'; // Blue for senior
    if (roleLevel >= RoleLevel.VERIFIED_DEALER)
        return '#22c55e'; // Green for verified
    return '#eab308'; // Yellow for basic/applicant
};
/**
 * Configuration export for easy access
 */
export const RoleConfig = {
    ASSIGNABLE_ROLES,
    SYSTEM_STATE_ROLES,
    DEPRECATED_ROLES,
    ALL_ACTIVE_ROLES,
    getAssignableRoleOptions,
    getAllRoleOptions,
    getRoleEnumKey,
    getRoleLevelFromKey,
    isAssignableRole,
    isSystemStateRole,
    isValidRoleName,
    getRoleColor
};
export default RoleConfig;
